import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    Chip,
    Avatar,
    Tooltip,
    Divider,
    TextField,
    Autocomplete,
    Button,
    Switch,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendIcon from '@mui/icons-material/Send';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useBrief, useBriefComments, useCreateBriefComment, useUploadCommentAttachments } from '../../hooks/useBriefs';
import { useSnackbar } from 'notistack';
import {
    PRIORITY_COLORS, STATUS_COLORS, colorForName, initialsFor, formatBudget,
    attachmentFileName, splitCommentBody, buildCommentBody, formatCommentTimestamp, labelForStatus,
} from './briefUiUtils';
import { formatDate } from '../plans/formatDate';

const fieldLabelSx = { fontSize: 12, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.4 };

// Kept in sync with MAX_COMMENT_ATTACHMENTS in pluginbackendnode's
// models/briefComment.js and the allowlist in helpers/s3BriefAttachments.js's
// isAllowedCommentAttachmentMimeType — this is just the early, friendlier
// check; the backend enforces the real limits.
const MAX_COMMENT_ATTACHMENTS = 5;
const MAX_COMMENT_ATTACHMENT_SIZE_MB = 5;
const ALLOWED_DOCUMENT_TYPES = new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

const findInvalidCommentFile = (files) => {
    for (const file of files) {
        const isAllowed = (file.type && file.type.startsWith('image/')) || ALLOWED_DOCUMENT_TYPES.has(file.type);
        if (!isAllowed) return `"${file.name}" isn't an allowed file type (images, PDF, or Word/Excel/PowerPoint)`;
        if (file.size > MAX_COMMENT_ATTACHMENT_SIZE_MB * 1024 * 1024)
            return `"${file.name}" is larger than ${MAX_COMMENT_ATTACHMENT_SIZE_MB}MB`;
    }
    return null;
};

/**
 * @param {boolean} open
 * @param {string|null} briefId
 * @param {() => void} onClose
 * @param {(brief: object) => void} onEdit - opens the full edit modal for this brief; closes this dialog first.
 * @param {string|null} currentUserId
 * @param {boolean} isSuperUser
 */
const BriefDetailDialog = ({ open, briefId, onClose, onEdit, currentUserId, isSuperUser = false }) => {
    const { data: brief, isLoading, isError, error } = useBrief(briefId);
    const { data: comments = [], isLoading: loadingComments } = useBriefComments(briefId);
    const createComment = useCreateBriefComment();
    const uploadAttachments = useUploadCommentAttachments();
    const { enqueueSnackbar } = useSnackbar();

    const [messageText, setMessageText] = useState('');
    const [mentionedUsers, setMentionedUsers] = useState([]);
    const [isUpdateRequest, setIsUpdateRequest] = useState(false);
    const [attachments, setAttachments] = useState([]);

    // Mirrors the backend's own mention pool exactly (routes/briefs.js's
    // briefParticipantIds) — createdby/assignee/reviewer, not the org roster,
    // so every option here is guaranteed to actually see the result. Excludes
    // yourself; mentioning yourself for an update is meaningless.
    const mentionOptions = useMemo(() => {
        if (!brief) return [];
        const byId = new Map();
        if (brief.createdBy?.id) byId.set(brief.createdBy.id, brief.createdBy);
        (brief.assignee || []).forEach((u) => byId.set(u.id, u));
        (brief.reviewer || []).forEach((u) => byId.set(u.id, u));
        byId.delete(currentUserId);
        return [...byId.values()];
    }, [brief, currentUserId]);

    // Creator, reviewer, or super user — mirrors hasFullBriefAccess in
    // routes/briefs.js. Gates the "Request update" toggle only; ordinary
    // commenting and mentioning stay open to any participant.
    const isFullAccess = useMemo(() => {
        if (!brief) return false;
        if (isSuperUser) return true;
        if (brief.createdBy?.id === currentUserId) return true;
        return (brief.reviewer || []).some((r) => r.id === currentUserId);
    }, [brief, currentUserId, isSuperUser]);

    const hasContent = mentionedUsers.length > 0 || messageText.trim().length > 0 || attachments.length > 0;
    const updateRequestNeedsMention = isUpdateRequest && mentionedUsers.length === 0;
    const canPost = hasContent && !updateRequestNeedsMention && !createComment.isPending;

    const resetComposer = useCallback(() => {
        setMessageText('');
        setMentionedUsers([]);
        setIsUpdateRequest(false);
        setAttachments([]);
    }, []);

    // Covers every way briefId can change while this dialog stays mounted —
    // closing (briefId → undefined), and also a direct jump from one brief's
    // detail straight to another's (e.g. a pasted /brief/:id link opened
    // while already viewing a different one), which wouldn't otherwise go
    // through handleClose at all and would leave a stale draft comment
    // sitting over the new brief's activity feed.
    useEffect(() => {
        resetComposer();
    }, [briefId, resetComposer]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleEditClick = useCallback(() => {
        onEdit(brief);
        handleClose();
    }, [onEdit, brief, handleClose]);

    const handlePost = useCallback(() => {
        if (!canPost) return;
        const body = buildCommentBody(mentionedUsers, messageText);
        createComment.mutate(
            { briefId, body, isUpdateRequest, attachments },
            {
                onSuccess: resetComposer,
                onError: (err) => {
                    enqueueSnackbar(err?.response?.data?.message || 'Failed to post comment', { variant: 'error' });
                },
            }
        );
    }, [canPost, mentionedUsers, messageText, isUpdateRequest, attachments, briefId, createComment, resetComposer, enqueueSnackbar]);

    // Uploads immediately (there's no "draft" concept for a comment's own
    // attachments — the parent brief already exists, so these go straight
    // under its id, see uploadCommentAttachments in useBriefs.js) and holds
    // the resulting URLs in local state until Post actually creates the
    // comment. If the composer is abandoned instead, these become orphaned
    // S3 objects — the same accepted tradeoff as the brief's own
    // "add attachment to an existing brief" flow.
    const handleFilesSelected = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // lets the same file be picked again later
        if (!files.length) return;

        if (attachments.length + files.length > MAX_COMMENT_ATTACHMENTS) {
            enqueueSnackbar(`Up to ${MAX_COMMENT_ATTACHMENTS} files per comment`, { variant: 'error' });
            return;
        }
        const invalidReason = findInvalidCommentFile(files);
        if (invalidReason) {
            enqueueSnackbar(invalidReason, { variant: 'error' });
            return;
        }

        uploadAttachments.mutate(
            { briefId, files },
            {
                onSuccess: (urls) => {
                    setAttachments((prev) => [...prev, ...urls]);
                    enqueueSnackbar('Attachment(s) uploaded', { variant: 'success' });
                },
                onError: (err) => {
                    enqueueSnackbar(err?.response?.data?.message || 'Failed to upload attachment', { variant: 'error' });
                },
            }
        );
    }, [briefId, attachments.length, uploadAttachments, enqueueSnackbar]);

    const handleRemoveAttachment = useCallback((url) => {
        setAttachments((prev) => prev.filter((a) => a !== url));
    }, []);

    const statusStyle = brief ? (STATUS_COLORS[brief.status] || STATUS_COLORS.Pending) : STATUS_COLORS.Pending;
    const priorityColor = brief ? (PRIORITY_COLORS[brief.priority] || PRIORITY_COLORS.Medium) : PRIORITY_COLORS.Medium;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                elevation: 0,
                sx: { borderRadius: '20px', overflow: 'hidden', border: '1px solid #E5E7EB', maxHeight: '88vh' },
            }}
        >
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress sx={{ color: '#FF5200' }} />
                </Box>
            ) : isError || !brief ? (
                <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Couldn't load this brief</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {error?.response?.data?.message || "It may have been deleted, or you don't have access to it."}
                    </Typography>
                    <Button variant="outlined" onClick={handleClose} sx={{ textTransform: 'none', borderRadius: '10px' }}>
                        Close
                    </Button>
                </Box>
            ) : (
                <>
                    <Box sx={{ height: 4, background: priorityColor, flexShrink: 0 }} />

                    {/* Header */}
                    <Box sx={{
                        px: 3, pt: 2.5, pb: 2, display: 'flex', alignItems: 'flex-start',
                        justifyContent: 'space-between', gap: 1.5, borderBottom: '1px solid #F3F4F6',
                    }}>
                        <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
                            <Box sx={{
                                width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.06))',
                                border: '1px solid rgba(255,82,0,0.18)',
                            }}>
                                <AssignmentOutlinedIcon sx={{ color: '#FF5200', fontSize: 18 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#111827', lineHeight: 1.3 }}>
                                    {brief.title}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: '#9CA3AF', mt: 0.2 }}>
                                    {brief.brand?.name || 'No brand'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={handleEditClick} sx={{
                                    color: '#9CA3AF', background: '#F9FAFB', border: '1px solid #E5E7EB',
                                    '&:hover': { background: '#FFF4EE', color: '#FF5200', borderColor: '#FFD6BE' },
                                }}>
                                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={handleClose} sx={{
                                color: '#9CA3AF', background: '#F9FAFB', border: '1px solid #E5E7EB',
                                '&:hover': { background: '#F3F4F6', color: '#374151' },
                            }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={{ overflowY: 'auto', px: 3, py: 2.5 }}>
                        {/* Status/priority chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                            <Chip label={labelForStatus(brief.status)} size="small" sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 700, fontSize: 11.5, height: 24 }} />
                            <Chip label={brief.priority} size="small" sx={{ bgcolor: `${priorityColor}14`, color: priorityColor, fontWeight: 700, fontSize: 11.5, height: 24 }} />
                            {brief.deliverableType && (
                                <Chip label={brief.deliverableType} size="small" sx={{ bgcolor: '#F3F4F6', color: '#4B5563', fontWeight: 600, fontSize: 11.5, height: 24 }} />
                            )}
                            {(brief.category || []).map((c) => (
                                <Chip key={c.id} label={c.name} size="small" sx={{ bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 600, fontSize: 11.5, height: 24 }} />
                            ))}
                        </Box>

                        {/* Field grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                            <Box>
                                <Typography sx={fieldLabelSx}>Deadline</Typography>
                                <Typography sx={{ fontSize: 14, mt: 0.25 }}>{formatDate(brief.deadline) || 'No date'}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={fieldLabelSx}>Budget</Typography>
                                <Typography sx={{ fontSize: 14, mt: 0.25 }}>₹{formatBudget(brief.budget)}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={fieldLabelSx}>Assignees</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    {(brief.assignee || []).length === 0 ? (
                                        <Typography sx={{ fontSize: 13.5, color: '#9CA3AF' }}>Unassigned</Typography>
                                    ) : brief.assignee.map((u) => (
                                        <Chip key={u.id} size="small" avatar={<Avatar sx={{ bgcolor: colorForName(u.name) + ' !important' }}>{initialsFor(u.name)}</Avatar>} label={u.name} sx={{ fontSize: 12.5 }} />
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={fieldLabelSx}>Reviewer</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    {(brief.reviewer || []).map((u) => (
                                        <Chip key={u.id} size="small" avatar={<Avatar sx={{ bgcolor: colorForName(u.name) + ' !important' }}>{initialsFor(u.name)}</Avatar>} label={u.name} sx={{ fontSize: 12.5 }} />
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        {brief.description && (
                            <Box sx={{ mb: 2.5 }}>
                                <Typography sx={fieldLabelSx}>Description</Typography>
                                <Typography sx={{ fontSize: 14, mt: 0.5, whiteSpace: 'pre-wrap', color: '#374151' }}>{brief.description}</Typography>
                            </Box>
                        )}

                        {(brief.referenceLinks || []).length > 0 && (
                            <Box sx={{ mb: 2.5 }}>
                                <Typography sx={fieldLabelSx}>Reference Links</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                                    {brief.referenceLinks.map((link) => (
                                        <Chip key={link} label={link} size="small" onClick={() => window.open(link, '_blank', 'noopener,noreferrer')} sx={{ cursor: 'pointer', maxWidth: 260 }} />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {(brief.attachments || []).length > 0 && (
                            <Box sx={{ mb: 2.5 }}>
                                <Typography sx={fieldLabelSx}>Attachments</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                                    {brief.attachments.map((url) => (
                                        <Chip key={url} label={attachmentFileName(url)} size="small" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')} sx={{ cursor: 'pointer', maxWidth: 240 }} />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Divider sx={{ my: 2.5 }} />

                        {/* Activity feed */}
                        <Typography sx={{ fontWeight: 700, fontSize: 14.5, mb: 1.5 }}>Activity</Typography>
                        {loadingComments ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={22} sx={{ color: '#FF5200' }} />
                            </Box>
                        ) : comments.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic', mb: 1 }}>
                                No activity yet — be the first to comment.
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mb: 2 }}>
                                {comments.map((comment) => (
                                    <Box key={comment.id} sx={{ display: 'flex', gap: 1.25 }}>
                                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700, bgcolor: colorForName(comment.author.name), flexShrink: 0 }}>
                                            {initialsFor(comment.author.name)}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{comment.author.name}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#9CA3AF' }}>{formatCommentTimestamp(comment.createdOn)}</Typography>
                                                {comment.isUpdateRequest && (
                                                    <Chip
                                                        icon={<CampaignOutlinedIcon sx={{ fontSize: '13px !important' }} />}
                                                        label="Requested an update"
                                                        size="small"
                                                        sx={{ bgcolor: '#FFF4EE', color: '#FF5200', fontWeight: 600, fontSize: 10.5, height: 20 }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: 13.5, color: '#374151', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                                                {splitCommentBody(comment.body, comment.mentions).map((piece, i) =>
                                                    piece.mentionId ? (
                                                        <Box key={i} component="span" sx={{ bgcolor: '#FFF4EE', color: '#FF5200', fontWeight: 600, borderRadius: '4px', px: 0.5 }}>
                                                            @{piece.name}
                                                        </Box>
                                                    ) : piece.url ? (
                                                        <a
                                                            key={i}
                                                            href={piece.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: '#FF5200', fontWeight: 600, wordBreak: 'break-all' }}
                                                        >
                                                            {piece.url}
                                                        </a>
                                                    ) : (
                                                        <span key={i}>{piece.text}</span>
                                                    )
                                                )}
                                            </Typography>
                                            {(comment.attachments || []).length > 0 && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                                                    {comment.attachments.map((url) => (
                                                        <Chip
                                                            key={url}
                                                            icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                                                            label={attachmentFileName(url)}
                                                            size="small"
                                                            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                                                            sx={{ cursor: 'pointer', maxWidth: 220 }}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Composer */}
                        <Box sx={{ mt: 1 }}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={mentionOptions}
                                getOptionLabel={(u) => u?.name ?? ''}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={mentionedUsers}
                                onChange={(_, value) => setMentionedUsers(value)}
                                renderInput={(params) => <TextField {...params} placeholder="Mention someone (optional)" />}
                                sx={{ mb: 1 }}
                            />
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                placeholder="Post an update or comment…"
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                sx={{ mb: 1 }}
                            />
                            <Box sx={{ mb: 1 }}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    startIcon={uploadAttachments.isPending ? <CircularProgress size={14} /> : <AttachFileIcon />}
                                    disabled={uploadAttachments.isPending || attachments.length >= MAX_COMMENT_ATTACHMENTS}
                                    sx={{ textTransform: 'none', borderRadius: '10px', borderColor: '#E5E7EB', color: '#374151' }}
                                >
                                    {uploadAttachments.isPending ? 'Uploading…' : 'Attach file'}
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                                        hidden
                                        onChange={handleFilesSelected}
                                    />
                                </Button>
                                {attachments.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                                        {attachments.map((url) => (
                                            <Chip
                                                key={url}
                                                icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                                                label={attachmentFileName(url)}
                                                size="small"
                                                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                                                onDelete={() => handleRemoveAttachment(url)}
                                                sx={{ cursor: 'pointer', maxWidth: 220 }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                {isFullAccess ? (
                                    <FormControlLabel
                                        control={<Switch size="small" checked={isUpdateRequest} onChange={(e) => setIsUpdateRequest(e.target.checked)} />}
                                        label={<Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>Request an update{updateRequestNeedsMention ? ' (mention someone above)' : ''}</Typography>}
                                    />
                                ) : <Box />}
                                <Button
                                    variant="contained"
                                    endIcon={createComment.isPending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                                    disabled={!canPost}
                                    onClick={handlePost}
                                    sx={{
                                        background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                                        borderRadius: '10px', textTransform: 'none', fontWeight: 700, boxShadow: 'none',
                                        '&.Mui-disabled': { background: '#F3F4F6', color: '#9CA3AF' },
                                    }}
                                >
                                    Post
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </>
            )}
        </Dialog>
    );
};

export default BriefDetailDialog;
